import { useEffect, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { Card } from '../../components/core/Card.jsx'
import { StatusDot } from '../../components/core/StatusDot.jsx'

// GET /billing/success. Stripe redirects here before its webhook lands, so for a
// moment a paid customer still reads as trialing. This page says what Stripe did,
// draws **one** screen whatever the account holds, and sends the customer on once
// the plan is actually theirs.
//
// **The prop decides when to leave, never what to draw**, settled 2026-10-09.
// The recorded rule was that this page must not read `subscription_status` at
// all, and its reasoning was right — branching on it would show a paying
// customer a trial screen for whatever interval Stripe's delivery takes. What
// that rule also prevented was *noticing*, which is a different thing from
// gating, and the cost of conflating them was this page: it carried
// `ASSUMED PROP: subscription_status` from the batch-3 integration, the
// controller never sent it, and `checkout_test` asserted it was nil. So the
// redirect branch was unreachable, the reload fired every two seconds for as
// long as the tab stayed open — four requests in nine seconds, measured — and
// the page claimed *"This page moves on to your plan when that confirmation
// lands"* while being unable to. The claim is true now.
//
// **Nothing here renders from the status**, which is what makes the narrower
// rule safe, and `checkout_test` holds it from the server side: the props of a
// trialing and an active account must differ in that key alone, so a plan name
// or a price appearing here fails by name.
//
// **The poll gives up**, and the old one did not. After the deadline it stops
// asking and says so, rather than leaving a pulse running over a request loop
// nobody is watching. The link to the plan is present from the first paint
// either way, so this page is never a dead end even if the webhook never
// arrives.
const POLL_MS = 2000
const GIVE_UP_AFTER_MS = 40000

// `next_path` is where the page goes once the plan lands — business setup for an
// account that has none yet, the Plan page otherwise.
export default function BillingSuccess({ subscription_status = null, next_path = '/billing' }) {
  const [waiting, setWaiting] = useState(true)

  useEffect(() => {
    if (subscription_status === 'active') {
      // `replace`, so Back does not return to a confirmation for a payment that
      // has already been confirmed.
      router.visit(next_path, { replace: true })
      return
    }

    const deadline = Date.now() + GIVE_UP_AFTER_MS
    const poll = setInterval(() => {
      if (Date.now() >= deadline) {
        clearInterval(poll)
        setWaiting(false)
        return
      }
      router.reload({ only: ['subscription_status'] })
    }, POLL_MS)

    return () => clearInterval(poll)
  }, [subscription_status, next_path])

  return (
    <>
      <Head title="Checkout complete" />
      <main className="mx-auto w-full max-w-2xl px-4 py-16 sm:px-6">
        <Card variant="signal" pad="lg">
          <div className="flex flex-col gap-4">
            <StatusDot
              state="visible"
              pulse={waiting}
              label={waiting ? 'Confirming with Stripe' : 'Still confirming'}
            />
            <h1 className="page-title">Checkout is complete</h1>
            {/*
              The last clause is conditional because the page stops polling at
              the deadline — so past it, "moves on when it does" would be the
              same false promise this page was rewritten to remove, in its own
              replacement. A sentence about what the page will do has to be
              true of the state the page is actually in.
            */}
            <p className="max-w-[60ch] text-[15px] text-[color:var(--text-body)]">
              Stripe confirms the payment to us separately, a moment behind you. Your plan is
              granted as soon as that confirmation reaches us
              {waiting ? ', and this page moves on when it does.' : '.'}
            </p>
            {!waiting && (
              <p className="max-w-[60ch] text-[13.5px] text-[color:var(--text-body)]">
                It is taking longer than usual. Your plan is granted whenever the confirmation
                lands, so there is nothing further to do here.
              </p>
            )}
            <p className="max-w-[60ch] text-[13.5px] text-[color:var(--text-muted)]">
              <Link href="/billing" className="underline underline-offset-4">
                Go to your plan
              </Link>{' '}
              to see the account as it is now.
            </p>
          </div>
        </Card>
      </main>
    </>
  )
}

BillingSuccess.layout = withSignedInLayout
